/**
 * Step 7 Mobile: Later Life & Estate
 * Deceased age sliders, care scenario cards, IHT strategies expandable.
 */

import { useState } from 'react';
import { isFeatureEnabled } from '../../config/features';
import { useForm, useFieldArray } from 'react-hook-form';
import type { Path } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { type StepTwilightYearsFormData, StepTwilightYearsSchema, type AllStepFormData } from '../../types/wizard';
import { WizardMobileShell } from './WizardMobileShell';

interface StepTwilightYearsProps {
  initialData?: Partial<AllStepFormData>;
  onNext: (data: StepTwilightYearsFormData) => void;
  onSave: (data: StepTwilightYearsFormData) => void;
  onBack?: () => void;
  lastSaved?: string;
}

export default function StepTwilightYearsMobile({ initialData, onNext, onSave, onBack, lastSaved }: Readonly<StepTwilightYearsProps>) {
  const personCount = initialData?.planType === 'JOINT' ? 2 : 1;

  const { register, handleSubmit, watch, setValue, control } = useForm<StepTwilightYearsFormData>({
    resolver: zodResolver(StepTwilightYearsSchema),
    defaultValues: {
      personDeceasedAges: initialData?.personDeceasedAges || Array.from({ length: personCount }, (_, i) => ({ personIndex: i, deceasedAge: 88 })),
      survivorSpendingPct: initialData?.survivorSpendingPct ?? 70,
      hasDirectDescendants: initialData?.hasDirectDescendants ?? null,
      includePensionsInEstate: initialData?.includePensionsInEstate ?? true,
      careScenarios: initialData?.careScenarios || [],
      ihtStrategies: initialData?.ihtStrategies || {
        annualGiftExemption: false,
        smallGiftRecipientsPerYear: 0,
        charitableLegacyEnabled: false,
        normalExpenditureFromIncome: false,
        annualPetAmount: 0,
        annualCharitableDonation: 0,
      },
      // Ring-fenced care fund: omission, never null (server schema rejects null)
      careFund: initialData?.careFund ?? undefined,
      careFundReleaseAge: initialData?.careFundReleaseAge ?? undefined,
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'careScenarios' });
  const [careExpanded, setCareExpanded] = useState(false);
  const [ihtExpanded, setIhtExpanded] = useState(false);
  const [estateExpanded, setEstateExpanded] = useState(false);

  const addCareScenario = () => append({
    scenarioId: crypto.randomUUID(),
    personIndex: 0,
    careType: 'HOME',
    annualCost: 1_000,
    startYear: 1,
    durationYears: 3,
    untilEndOfLife: true,
    inflationPremiumPct: 2,
    sellPrimaryResidenceOnCareStart: false,
  });

  const handleBlur = () => onSave(watch());

  const ages = watch('personDeceasedAges');
  const survivorPct = watch('survivorSpendingPct');

  return (
    <WizardMobileShell step={7} totalSteps={10} title="Later Life & Estate" onBack={onBack} onContinue={() => handleSubmit(onNext)()} lastSaved={lastSaved}>
      <div className="mobile-step-form">
        <fieldset className="mobile-fieldset">
          <legend className="mobile-label">Life Expectancy</legend>
          {Array.from({ length: personCount }, (_, i) => (
            <div key={i}>
              <label className="mobile-label">
                {personCount === 2 ? `Person ${i + 1}` : 'You'}: {ages?.[i]?.deceasedAge ?? 88}
              </label>
              <input type="range" className="mobile-slider" min={65} max={100} value={ages?.[i]?.deceasedAge ?? 88}
                onChange={e => { setValue(`personDeceasedAges.${i}`, { personIndex: i, deceasedAge: parseInt(e.target.value, 10) }); handleBlur(); }} />
            </div>
          ))}
        </fieldset>

        {(isFeatureEnabled('cashReserves') || (initialData?.careFund ?? 0) > 0) && (<>
        <label htmlFor="careFund" className="mobile-label">Set aside for later-life care (£)</label>
        <span className="mobile-hint">Kept in cash, in today&apos;s money, and released as care is paid for. With no care scenario and no backstop age it stays ring-fenced for the whole plan.</span>
        <input id="careFund" type="number" min={0} className="mobile-input" {...register('careFund', { valueAsNumber: true })} onBlur={handleBlur} />

        <label htmlFor="careFundReleaseAge" className="mobile-label">Backstop age</label>
        <span className="mobile-hint">Frees the fund once everyone still living has reached this age, whether or not care has started</span>
        <input id="careFundReleaseAge" type="number" min={50} max={120} className="mobile-input" placeholder="optional" {...register('careFundReleaseAge', { valueAsNumber: true })} onBlur={handleBlur} />
        </>)}

        <label className="mobile-label">Survivor spending: {survivorPct}%</label>
        <input type="range" className="mobile-slider" min={50} max={100} value={survivorPct}
          onChange={e => { setValue('survivorSpendingPct', parseInt(e.target.value, 10)); handleBlur(); }} />

        <label className="mobile-label">Has direct descendants?</label>
        <div className="mobile-segmented-control">
          {[{ k: 'yes', v: true as boolean | null, l: 'Yes' }, { k: 'no', v: false as boolean | null, l: 'No' }, { k: 'unsure', v: null as null, l: 'Not sure' }].map(o => (
            <label key={o.k} className={`mobile-segment${watch('hasDirectDescendants') === o.v ? ' mobile-segment--active' : ''}`}>
              <input type="radio" className="mobile-segment-input" value={o.k} onChange={() => { setValue('hasDirectDescendants', o.v); handleBlur(); }} />
              <span>{o.l}</span>
            </label>
          ))}
        </div>

        {/* Care Scenarios */}
        <button type="button" className="mobile-expand-toggle" style={{ fontWeight: 700 }} onClick={() => setCareExpanded(c => !c)}>
          {careExpanded ? '▾' : '▸'} Care Scenarios ({fields.length})
        </button>
        {careExpanded && (
          <>
            {fields.map((field, i) => (
              <div key={field.id} className="mobile-person-card" style={{ padding: '12px 16px' }}>
                <label className="mobile-label">Annual cost (£)</label>
                <input type="number" className="mobile-input" {...register(`careScenarios.${i}.annualCost` as Path<StepTwilightYearsFormData>, { valueAsNumber: true })} onBlur={handleBlur} />
                <label className="mobile-label">Start year</label>
                <input type="number" className="mobile-input" {...register(`careScenarios.${i}.startYear` as Path<StepTwilightYearsFormData>, { valueAsNumber: true })} onBlur={handleBlur} />
                <label className="mobile-label">Care type</label>
                <div className="mobile-segmented-control">
                  {(['HOME', 'RESIDENTIAL', 'NURSING'] as const).map(t => (
                    <label key={t} className={`mobile-segment${(watch('careScenarios')?.[i]?.careType ?? 'HOME') === t ? ' mobile-segment--active' : ''}`}>
                      <input type="radio" className="mobile-segment-input" value={t}
                        {...register(`careScenarios.${i}.careType` as Path<StepTwilightYearsFormData>)}
                        onChange={e => { void register(`careScenarios.${i}.careType` as Path<StepTwilightYearsFormData>).onChange(e); handleBlur(); }}
                      />
                      <span>{t === 'HOME' ? 'Home' : t === 'RESIDENTIAL' ? 'Residential' : 'Nursing'}</span>
                    </label>
                  ))}
                </div>
                <label className="mobile-label">Duration (years)</label>
                <input type="number" className="mobile-input" min={1} max={30}
                  {...register(`careScenarios.${i}.durationYears` as Path<StepTwilightYearsFormData>, { valueAsNumber: true })}
                  onBlur={handleBlur}
                />
                <label className="mobile-label">Extra annual cost rise above inflation (%)</label>
                <span className="mobile-hint">Care costs often rise faster than general prices. Leave at 2% if unsure.</span>
                <input type="number" className="mobile-input" min={0} max={10} step={0.25}
                  {...register(`careScenarios.${i}.inflationPremiumPct` as Path<StepTwilightYearsFormData>, { valueAsNumber: true })}
                  onBlur={handleBlur}
                />
                <button type="button" className="mobile-holding-card__delete" onClick={() => remove(i)}>✕ Remove</button>
              </div>
            ))}
            <button type="button" className="mobile-btn mobile-btn--outline mobile-btn--full" onClick={addCareScenario}>
              + Add Care Scenario
            </button>
          </>
        )}

        {/* IHT Strategies */}
        <button type="button" className="mobile-expand-toggle" onClick={() => setIhtExpanded(!ihtExpanded)}>
          {ihtExpanded ? '▾' : '▸'} IHT Strategies <span title="Inheritance Tax">ⓘ</span>
        </button>
        {ihtExpanded && (
          <div className="mobile-expand-section">
            <label className="auth-mobile__checkbox">
              <input type="checkbox" {...register('ihtStrategies.annualGiftExemption')} onChange={e => { void register('ihtStrategies.annualGiftExemption').onChange(e); handleBlur(); }} />
              <span>Annual gift exemption (£3,000/year)</span>
            </label>
            <label className="auth-mobile__checkbox">
              <input type="checkbox" {...register('ihtStrategies.charitableLegacyEnabled')} onChange={e => { void register('ihtStrategies.charitableLegacyEnabled').onChange(e); handleBlur(); }} />
              <span>Charitable legacy</span>
            </label>
            <label className="auth-mobile__checkbox">
              <input type="checkbox" {...register('ihtStrategies.normalExpenditureFromIncome')} onChange={e => { void register('ihtStrategies.normalExpenditureFromIncome').onChange(e); handleBlur(); }} />
              <span>Normal expenditure from income</span>
            </label>
            <label className="mobile-label">Gifts to family (£/year)</label>
            <span className="mobile-hint">Large gifts given 7+ years before death are usually Inheritance Tax free.</span>
            <input type="number" className="mobile-input" {...register('ihtStrategies.annualPetAmount', { valueAsNumber: true })} onBlur={handleBlur} />
            <label className="mobile-label">Small gift recipients per year</label>
            <input type="number" className="mobile-input" min={0}
              {...register('ihtStrategies.smallGiftRecipientsPerYear', { valueAsNumber: true })}
              onBlur={handleBlur}
            />
          </div>
        )}

        {/* Estate Distribution */}
        <button type="button" className="mobile-expand-toggle" onClick={() => setEstateExpanded(!estateExpanded)}>
          {estateExpanded ? '▾' : '▸'} Estate Distribution
        </button>
        {estateExpanded && (
          <div className="mobile-expand-section">
            <label className="mobile-label">Annual charitable donation (£)</label>
            <input type="number" className="mobile-input" min={0}
              {...register('ihtStrategies.annualCharitableDonation', { valueAsNumber: true })}
              onBlur={handleBlur}
            />
          </div>
        )}

        <label className="auth-mobile__checkbox">
          <input type="checkbox" {...register('includePensionsInEstate')} onChange={e => { void register('includePensionsInEstate').onChange(e); handleBlur(); }} />
          <span>Include pensions in estate value</span>
        </label>
      </div>
    </WizardMobileShell>
  );
}
